import { useState } from 'react'
import { Search, Plus, Building2, Users, ChevronRight } from 'lucide-react'
import { TenantSummary } from '../../../services/api'

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  starter: 'bg-blue-100 text-blue-700',
  trial: 'bg-orange-100 text-orange-700',
  pro: 'bg-yellow-100 text-yellow-700',
  enterprise: 'bg-purple-100 text-purple-700',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-orange-100 text-orange-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
}

interface Props {
  tenants: TenantSummary[]
  loading: boolean
  onSelectCompany: (tenantId: string) => void
  onNewCompany: () => void
}

export default function AdminCompanyList({ tenants, loading, onSelectCompany, onNewCompany }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')

  const filtered = tenants.filter((t) => {
    const matchesSearch =
      !searchQuery ||
      t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.tenant_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.admin_email?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = statusFilter === 'all' || t.status === statusFilter
    const matchesPlan = planFilter === 'all' || t.plan === planFilter

    return matchesSearch && matchesStatus && matchesPlan
  })

  function formatDate(dateStr: string) {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 outline-none"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
        >
          <option value="all">All Plans</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="trial">Trial</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>

        <button
          onClick={onNewCompany}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors ml-auto"
        >
          <Plus className="w-4 h-4" />
          New Company
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">
            {searchQuery || statusFilter !== 'all' || planFilter !== 'all'
              ? 'No companies match your filters'
              : 'No companies yet. Create your first company to get started.'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Company</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Plan</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Owner</th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Users</th>
                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Messages</th>
                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Created</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((t) => (
                <tr
                  key={t.tenant_id}
                  onClick={() => onSelectCompany(t.tenant_id)}
                  className="hover:bg-amber-50/30 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                        <p className="text-xs text-gray-400 truncate font-mono">{t.tenant_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase ${PLAN_COLORS[t.plan] || PLAN_COLORS.free}`}>
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${STATUS_COLORS[t.status] || STATUS_COLORS.active}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-gray-600 truncate">{t.admin_email}</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
                      <Users className="w-3.5 h-3.5 text-gray-400" />
                      {Object.keys(t.user_roles || {}).length + 1}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-sm text-gray-600">{(t.usage?.messages_used || 0).toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-xs text-gray-400">{formatDate(t.created_at)}</span>
                  </td>
                  <td className="px-3 py-3">
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Count */}
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-gray-400">
          Showing {filtered.length} of {tenants.length} companies
        </p>
      )}
    </div>
  )
}
